// ── Map onboarding cluster ──
// Extracted verbatim from MapCanvas.tsx as FOUR hooks. They are deliberately
// NOT merged into one: in MapCanvas these effects sit at four different points
// in the effect order, interleaved with unrelated effects, and that order is
// load-bearing (they gate on `isPixiAppReady` / `markersReady` and on modal
// state written by each other). Every hook is called from its original
// position, so the sequence is unchanged.
//
// Bodies are byte-identical to the originals; every closure dependency is a
// parameter under its ORIGINAL name.
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import type { MapObject } from '../../models/mapObject';
import type { UserData } from '../../models/user';
import type { MapModalName, MapModalPayload } from '../../stores/useMapModalStore';
import type { HighlightManager } from './controller/HighlightManager';
import { mapSceneSingleton } from './mapScene';
import { asTickerBound } from './render/mapPixiExtensions';
import { claimOnboardingRewardUserClaimOnboardingRewardPost } from '../../client';
import { patchOnboardingStateOnServer } from '../../utils/net/onboardingApi';
import { impactOccurred } from '../../utils/telegram/hapticFeedback';
import useMapModalStore from '../../stores/useMapModalStore';
import useMapOnboardingStore from '../../stores/useMapOnboardingStore';

/** Shape of the `user_onboarding_kv` payload the effects below read. */
type OnboardingQueryData = {
  onboarding_completed?: boolean | null;
  tap_onboarding_month?: string | null;
  map_welcome_started_at_ms?: number | null;
  zoom_hint_shown?: boolean | null;
} | undefined;

export interface UseOnboardingFirstMoveParams {
  /** By value — effect deps. */
  isPixiAppReady: boolean;
  onboardingQueryData: OnboardingQueryData;
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  hasTapDone: () => boolean;
  queryClient: QueryClient;
}

/** Records the timestamp of the user's very first map pan/zoom. */
export function useOnboardingFirstMove({
  isPixiAppReady,
  onboardingQueryData,
  sceneRef,
  hasTapDone,
  queryClient,
}: UseOnboardingFirstMoveParams): void {
  // ── Onboarding step 0 trigger: record first map movement time ─────────────
  useEffect(() => {
    if (!isPixiAppReady) return;
    if (hasTapDone()) return;
    if (!sceneRef.current.pixi.viewport) return;

    const viewport = sceneRef.current.pixi.viewport;
    let recorded = false;

    const recordStartedAt = () => {
      if (recorded) return;
      recorded = true;
      if (onboardingQueryData?.map_welcome_started_at_ms) return;
      // Тільки час першого руху — для App.tsx (forced welcome після 4с). Не ставити
      // map_welcome_month тут: інакше hasMapWelcomeDone стає true без модалки з
      // туторіалом/нагородою (типово після GM reset, якщо користувач злегка зсунув мапу).
      patchOnboardingStateOnServer({
        map_welcome_started_at_ms: Date.now(),
      }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['onboarding-state'] }).catch(() => {});
    };

    viewport.on('moved', recordStartedAt);
    viewport.on('zoomed', recordStartedAt);

    return () => {
      viewport.off('moved', recordStartedAt);
      viewport.off('zoomed', recordStartedAt);
    };
    // Тригер: pixi ready + month/started_at примітиви (не цілий onboardingQueryData).
    // hasTapDone — нова функція щорендеру з useOnboardingState; gate вже покритий
    // tap_onboarding_month. queryClient/sceneRef стабільні — додаємо.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasTapDone unstable each render
  }, [isPixiAppReady, onboardingQueryData?.tap_onboarding_month, onboardingQueryData?.map_welcome_started_at_ms, queryClient, sceneRef]);
}

export interface UseOnboardingFlowParams {
  /** Modal / flow flags — by value, effect deps. */
  isMapOnboardingModalOpen: boolean;
  isTutorialRewardModalOpen: boolean;
  isMapWelcomeModalOpen: boolean;
  isOnboardingZooming: boolean;
  isOnboardingBuildHousePopupOpen: boolean;
  isPlotFreeModalOpen: boolean;
  isPlotFreeModalOnboarding: boolean;
  onboardingQueryData: OnboardingQueryData;
  isPixiAppReady: boolean;
  hasOnboardingCompleted: () => boolean;
  hasTapDone: () => boolean;
  hasMapWelcomeDoneForMonth: () => boolean;
  hasMapTutorialDoneForMonth: () => boolean;
  queryClient: QueryClient;
  openModal: (name: MapModalName, payload?: Partial<MapModalPayload>) => void;
  navigate: (to: string, opts?: { replace?: boolean }) => void;
  setFirstVisitPhase: (phase: 'zoom_hint' | null) => void;
}

/**
 * Two adjacent effects, kept in their original order inside one hook:
 * (1) blocking the bottom nav while onboarding is in progress;
 * (2) driving the map-welcome / map-tutorial / zoom-hint sequence.
 * `zoomHintSequenceStartedRef` had no other consumer, so it moved here.
 */
export function useOnboardingFlow({
  isMapOnboardingModalOpen,
  isTutorialRewardModalOpen,
  isMapWelcomeModalOpen,
  isOnboardingZooming,
  isOnboardingBuildHousePopupOpen,
  isPlotFreeModalOpen,
  isPlotFreeModalOnboarding,
  onboardingQueryData,
  isPixiAppReady,
  hasOnboardingCompleted,
  hasTapDone,
  hasMapWelcomeDoneForMonth,
  hasMapTutorialDoneForMonth,
  queryClient,
  openModal,
  navigate,
  setFirstVisitPhase,
}: UseOnboardingFlowParams): void {
  const zoomHintSequenceStartedRef = useRef(false);

  // Block bottom nav while onboarding is in progress.
  // Single source of truth: onboarding_completed (set when user claims tutorial reward).
  useEffect(() => {
    const shouldBlockNav =
      isMapOnboardingModalOpen ||
      isTutorialRewardModalOpen ||
      isMapWelcomeModalOpen ||
      isOnboardingZooming ||
      isOnboardingBuildHousePopupOpen ||
      (isPlotFreeModalOpen && isPlotFreeModalOnboarding) ||
      (!!onboardingQueryData && !hasOnboardingCompleted());

    if (!shouldBlockNav) return;

    let navBar: Element | null = document.querySelector('.bottom-navigation-bar');
    let observer: MutationObserver | null = null;

    const handler = (e: Event) => {
      const target = e.target as HTMLElement;
      const button = target.closest('button[data-nav-path]');
      if (!button) return;
      e.preventDefault();
      e.stopPropagation();
    };

    const attach = (el: Element) => {
      el.addEventListener('click', handler, true);
      el.addEventListener('touchend', handler, true);
    };
    const detach = (el: Element) => {
      el.removeEventListener('click', handler, true);
      el.removeEventListener('touchend', handler, true);
    };

    if (navBar) {
      attach(navBar);
    } else {
      // Nav bar may mount a tick later than MapCanvas. Wait for it instead of
      // missing the attach silently.
      observer = new MutationObserver(() => {
        const el = document.querySelector('.bottom-navigation-bar');
        if (el) {
          navBar = el;
          attach(el);
          observer?.disconnect();
          observer = null;
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      if (navBar) detach(navBar);
    };
    // hasOnboardingCompleted — нова fn щорендеру; gate через onboardingQueryData
    // (вже в deps) + виклик у body. Не додаємо fn → re-attach listeners щорендер.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasOnboardingCompleted unstable
  }, [
    isMapOnboardingModalOpen,
    isTutorialRewardModalOpen,
    isMapWelcomeModalOpen,
    isOnboardingZooming,
    isOnboardingBuildHousePopupOpen,
    isPlotFreeModalOpen,
    isPlotFreeModalOnboarding,
    onboardingQueryData,
  ]);

  useEffect(() => {
    if (!isPixiAppReady) return;
    // If onboarding already fully done — nothing to do on map
    if (hasOnboardingCompleted()) return;

    if (hasTapDone()) {
      if (!hasMapWelcomeDoneForMonth()) {
        const currentMonth = new Date().getFullYear() + "_" + String(new Date().getMonth() + 1).padStart(2, "0");
        patchOnboardingStateOnServer({ map_welcome_month: currentMonth }).catch(() => {});
        queryClient.invalidateQueries({ queryKey: ['onboarding-state'] }).catch(() => {});
      }
      if (!hasMapTutorialDoneForMonth()) {
        const timer = setTimeout(() => {
          // Never stack over / restart a modal that is already up (this effect
          // re-runs on every onboarding-state refetch).
          if (useMapModalStore.getState().activeModal !== null) return;
          openModal('mapOnboarding');
          posthog.capture('onboarding_map_tutorial_shown');
        }, 1000);
        return () => clearTimeout(timer);
      }
      return;
    }

    // Tap not done yet. If they somehow landed on /map before completing tap
    // (e.g. stuck state from partial prior session) — send them back to tap.
    if (hasMapWelcomeDoneForMonth()) {
      navigate('/tap', { replace: true });
      return;
    }

    // The first-visit zoom hint opens the welcome modal itself when it ends —
    // don't race it with a second opener (that produced a duplicate welcome
    // which later popped up again in the middle of the map tutorial).
    if (zoomHintSequenceStartedRef.current || useMapOnboardingStore.getState().firstVisitPhase !== null) {
      return;
    }
    const zoomHintAlreadyShown = !!onboardingQueryData?.zoom_hint_shown;
    if (!zoomHintAlreadyShown) {
      // First ever map visit: start zoom hint → pause → welcome (driven by onAnimationEnd)
      zoomHintSequenceStartedRef.current = true;
      patchOnboardingStateOnServer({ zoom_hint_shown: true }).catch(() => {});
      setFirstVisitPhase('zoom_hint');
      return;
    }
    const timer = setTimeout(() => {
      if (useMapModalStore.getState().activeModal !== null) return;
      openModal('mapWelcome');
      posthog.capture('onboarding_map_welcome_shown');
    }, 500);
    return () => clearTimeout(timer);
    // has*/openModal/navigate/setFirstVisitPhase/queryClient: has* — нові fn щорендеру
    // з useOnboardingState; openModal/navigate можуть бути нестабільні. onboardingQueryData
    // уже в deps — підтягує актуальні flags; повторний openModal при identity-change
    // дасть дубль welcome/tutorial таймерів.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onboardingQueryData drives flow
  }, [isPixiAppReady, onboardingQueryData]);
}

export interface UseTutorialRewardClaimParams {
  isTutorialRewardModalOpen: boolean;
  queryClient: QueryClient;
  setOpenOnboardingPlotFreeAfterReward: (v: boolean) => void;
  closeModal: () => void;
}

/** 'claim' — the reward is being claimed (spinner); 'done' — show the reward. */
export type TutorialRewardPhase = 'claim' | 'done';

export interface UseTutorialRewardClaimResult {
  rewardPhase: TutorialRewardPhase;
  setRewardPhase: React.Dispatch<React.SetStateAction<TutorialRewardPhase>>;
  totalPearlsAwarded: number;
  totalExpAwarded: number;
  /** Paid with the claim when bot messages were already allowed: the doubling, no prompt. */
  notifyBonus: { pearls: number; exp: number };
}

/** Auto-claims the onboarding reward as soon as the reward modal opens. */
export function useTutorialRewardClaim({
  isTutorialRewardModalOpen,
  queryClient,
  setOpenOnboardingPlotFreeAfterReward,
  closeModal,
}: UseTutorialRewardClaimParams): UseTutorialRewardClaimResult {
  // Deferred trigger: reward modal must wait until RealEstateModal is closed,
  // otherwise it visually stacks on top of the house-picker.
  const [tutorialRewardClaiming, setTutorialRewardClaiming] = useState(false);
  const [rewardPhase, setRewardPhase] = useState<TutorialRewardPhase>('claim');
  const [totalPearlsAwarded, setTotalPearlsAwarded] = useState(0);
  const [totalExpAwarded, setTotalExpAwarded] = useState(0);
  const [notifyBonus, setNotifyBonus] = useState({ pearls: 0, exp: 0 });

  // Auto-claim onboarding reward when tutorial reward modal opens
  useEffect(() => {
    if (!isTutorialRewardModalOpen || rewardPhase !== 'claim') return;
    if (tutorialRewardClaiming) return;
    let cancelled = false;
    (async () => {
      setTutorialRewardClaiming(true);
      try {
        const { data: result } = await claimOnboardingRewardUserClaimOnboardingRewardPost({ throwOnError: true });
        if (cancelled) return;
        queryClient.setQueryData<UserData>(['userData'], (old) => {
          if (!old) return old;
          return { ...old, currency_balance: result.new_balance, exp_current: result.new_exp };
        });
        queryClient.invalidateQueries({ queryKey: ['userData'] });
        await queryClient.refetchQueries({ queryKey: ['onboarding-state'] });
        setTotalPearlsAwarded(result.pearls_awarded);
        setTotalExpAwarded(result.exp_awarded);
        setNotifyBonus({ pearls: result.notify_bonus_pearls ?? 0, exp: result.notify_bonus_exp ?? 0 });
        setRewardPhase('done');
        posthog.capture('onboarding_tutorial_reward_claimed', { pearls: result.pearls_awarded, exp: result.exp_awarded });
        // The reward card itself now asks for bot messages (NotifyPermissionPrompt).
      } catch {
        if (cancelled) return;
        setOpenOnboardingPlotFreeAfterReward(false);
        closeModal();
      } finally {
        if (!cancelled) setTutorialRewardClaiming(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- claim once when reward modal opens; omit claim helpers
  }, [isTutorialRewardModalOpen]);

  return {
    rewardPhase,
    setRewardPhase,
    totalPearlsAwarded,
    totalExpAwarded,
    notifyBonus,
  };
}

export interface UseOnboardingZoomToPlotParams {
  /** By value — effect deps. */
  location: { pathname: string };
  shouldZoomToPlot: boolean;
  markersReady: boolean;
  isPixiAppReady: boolean;
  plots: MapObject[];
  cityhalls: MapObject[];
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  highlightManagerRef: MutableRefObject<HighlightManager | null>;
  setIsOnboardingZooming: (v: boolean) => void;
  setShouldZoomToPlot: (v: boolean) => void;
  setSelectedMapObject: (obj: MapObject | null) => void;
  setPlotFreeOnboarding: (v: boolean) => void;
  setOpenOnboardingPlotFreeAfterReward: (v: boolean) => void;
  openModal: (name: MapModalName, payload?: Partial<MapModalPayload>) => void;
  hasOnboardingCompleted: () => boolean;
}

/** Highlight → zoom-in → haptic → open the free-plot / reward modal. */
export function useOnboardingZoomToPlot({
  location,
  shouldZoomToPlot,
  markersReady,
  isPixiAppReady,
  plots,
  cityhalls,
  sceneRef,
  highlightManagerRef,
  setIsOnboardingZooming,
  setShouldZoomToPlot,
  setSelectedMapObject,
  setPlotFreeOnboarding,
  setOpenOnboardingPlotFreeAfterReward,
  openModal,
  hasOnboardingCompleted,
}: UseOnboardingZoomToPlotParams): void {
  // Наближення до вільної ділянки після закриття онбордінгу (по кліку "Start Your Story")
  useEffect(() => {
    // Не запускаємо анімацію, якщо користувач не на мапі (наприклад, під час показу гіфки)
    if (location.pathname !== '/map') {
      return;
    }
    
    if (!shouldZoomToPlot || !markersReady || !sceneRef.current.layers.highlight || !sceneRef.current.pixi.app || !isPixiAppReady) {
      // Видаляємо підсвічування, якщо умова не активна
      if (sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
        const hl = asTickerBound(sceneRef.current.sprites.mapObjectHighlight);
        if (sceneRef.current.pixi.app && hl?.tickerFunction) {
          sceneRef.current.pixi.app.ticker.remove(hl.tickerFunction);
        }
        sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
        sceneRef.current.sprites.mapObjectHighlight.destroy();
        sceneRef.current.sprites.mapObjectHighlight = null;
        mapSceneSingleton.mapObjectHighlight = null;
      }
      return;
    }

    // Перевіряємо наявність необхідних refs
    if (!sceneRef.current.markers.plots || !sceneRef.current.markers.mapObjects) {
      return;
    }

    // Спочатку шукаємо вільну ділянку
    let targetObject: MapObject | null = null;
    let targetMarker: PIXI.Container | null = null;

    if (plots.length > 0) {
      // Якщо є вільні ділянки, беремо першу
      const plot = plots[0];
      if (plot && plot.id !== undefined) {
        const marker = sceneRef.current.markers.plots.get(plot.id);
        if (marker && !marker.destroyed) {
          targetObject = plot;
          targetMarker = marker;
        }
      }
    }

    // Якщо не знайшли вільну ділянку, шукаємо мерію
    if (!targetObject && cityhalls.length > 0) {
      const cityhall = cityhalls[0];
      if (cityhall && cityhall.id !== undefined) {
        const marker = sceneRef.current.markers.mapObjects.get(cityhall.id);
        if (marker && !marker.destroyed) {
          targetObject = cityhall;
          targetMarker = marker;
        }
      }
    }

    // Якщо знайшли об'єкт з валідними координатами, підсвічуємо та зумуємо до нього
    if (targetObject && targetMarker &&
        typeof targetObject.x === 'number' &&
        typeof targetObject.y === 'number' &&
        !isNaN(targetObject.x) &&
        !isNaN(targetObject.y)) {
      try {
        // Анімація: highlight → zoom-in → пауза → вібрація → модалка
        if (sceneRef.current.pixi.viewport && sceneRef.current.pixi.viewport.animate) {
          const capturedTarget = targetObject;
          const capturedMarker = targetMarker;
          let cancelled = false;
          const timers: ReturnType<typeof setTimeout>[] = [];
          const after = (ms: number, fn: () => void) => {
            const t = setTimeout(() => { if (!cancelled) fn(); }, ms);
            timers.push(t);
          };

          // Підсвічуємо ділянку одразу
          highlightManagerRef.current!.apply(capturedTarget, capturedMarker);

          // Zoom-in до ділянки (блокуємо взаємодію)
          setIsOnboardingZooming(true);

          const handleZoomInEnd = () => {
            if (cancelled) return;
            // Keep isOnboardingZooming=true until modal opens to block interaction
            // Пауза → вібрація ("тап" на ділянку) → пауза → відкриваємо модалку
            // ВАЖЛИВО: setShouldZoomToPlot(false) викликається в кінці, щоб не скасувати pending таймери
            after(500, () => {
              impactOccurred('medium');
              after(250, () => {
                setIsOnboardingZooming(false);
                const rewardAlreadyClaimed = hasOnboardingCompleted();
                if (capturedTarget.type === 'plot' && capturedTarget.owner_id === null) {
                  setSelectedMapObject(capturedTarget);
                  setPlotFreeOnboarding(true);
                  if (rewardAlreadyClaimed) {
                    // Reward already claimed (e.g. replay edge case) — skip reward, show plot directly
                    openModal('plotFree');
                  } else {
                    setOpenOnboardingPlotFreeAfterReward(true);
                    openModal('tutorialReward');
                  }
                } else {
                  if (!rewardAlreadyClaimed) {
                    openModal('tutorialReward');
                  }
                }
                setShouldZoomToPlot(false);
              });
            });
          };

          // Onboarding has priority — force-acquire the singleton lock
          sceneRef.current.viewport.animating = true;
          sceneRef.current.pixi.viewport.once('animate-end', () => { sceneRef.current.viewport.animating = false; });
          sceneRef.current.pixi.viewport.once('animate-end', handleZoomInEnd);
          sceneRef.current.pixi.viewport.animate({
            position: { x: capturedTarget.x, y: capturedTarget.y },
            scale: 1.5,
            time: 1600,
          });

          return () => {
            cancelled = true;
            timers.forEach(clearTimeout);
            // .current у cleanup навмисно: viewport живий на момент cancel zoom, не mount snapshot.
            /* eslint-disable react-hooks/exhaustive-deps -- cleanup needs live viewport, not mount snapshot */
            if (sceneRef.current.pixi.viewport) {
              sceneRef.current.pixi.viewport.off('animate-end', handleZoomInEnd);
            }
            /* eslint-enable react-hooks/exhaustive-deps */
            setIsOnboardingZooming(false);
          };
        }
      } catch (error) {
        console.error('[MapCanvas] Error during onboarding zoom:', error);
        setIsOnboardingZooming(false);
        setShouldZoomToPlot(false);
      }
    } else {
      // No valid target found — unblock interaction
      console.warn('[MapCanvas] Onboarding zoom: no target object found, skipping');
      setIsOnboardingZooming(false);
      // No plot/cityhall to zoom to — show reward immediately (skip free-plot flow).
      if (!hasOnboardingCompleted()) {
        openModal('tutorialReward');
      }
      setShouldZoomToPlot(false);
    }
    // Тригер: shouldZoom + markers + plots/cityhalls + route. hasOnboardingCompleted /
    // openModal / set* — нестабільні або setState; sceneRef/highlightManagerRef —
    // refs (стабільні identity). Додавання has*/openModal → повторний zoom/reward.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- zoom trigger deps only
  }, [shouldZoomToPlot, markersReady, plots, cityhalls, isPixiAppReady, location.pathname, sceneRef, highlightManagerRef]);
}
